/**
 * Package List Page
 * Package System — card-based list with type tabs, search, and filtering
 *
 * Features:
 *   - Type tabs: RENTAL, SALES, SERVICE, MIXED, PROMOTIONAL, FRANCHISE (with counts)
 *   - Card-based layout with badges, pricing, status
 *   - Search input
 *   - Actions: Details, Edit, Copy, Delete
 *   - Seasonal countdown for PROMOTIONAL packages
 */

import type { Package, PackageType } from '@/api/packages';
import { Button, Card, CardContent, Input } from '@/components/ui';
import { useDeletePackage, usePackages } from '@/hooks/use-packages';
import { formatHuf } from '@/lib/utils';
import { useTranslation } from '@kgc/i18n';
import { Badge } from '@kgc/ui';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

// ─── Package Type Config ────────────────────────────────────────────────────

type TabKey = 'ALL' | PackageType;

const PACKAGE_TYPES: PackageType[] = [
  'RENTAL',
  'SALES',
  'SERVICE',
  'MIXED',
  'PROMOTIONAL',
  'FRANCHISE',
];

const TYPE_COLORS: Record<PackageType, string> = {
  RENTAL: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
  SALES: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  SERVICE: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
  MIXED: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300',
  PROMOTIONAL: 'bg-pink-100 text-pink-800 dark:bg-pink-900/30 dark:text-pink-300',
  FRANCHISE: 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900/30 dark:text-cyan-300',
};

const STATUS_COLORS = {
  ACTIVE: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  INACTIVE: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
} as const;

// ─── Helpers ─────────────────────────────────────────────────────────────────

function getSeasonalCountdown(validTo: string | null | undefined): number | null {
  if (!validTo) return null;
  const diff = new Date(validTo).getTime() - Date.now();
  if (diff <= 0) return 0;
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
}

// ─── Component ───────────────────────────────────────────────────────────────

export function PackageListPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabKey>('ALL');
  const [searchTerm, setSearchTerm] = useState('');

  // Fetch ALL packages (no type filter) — client-side filtering for correct tab counts
  const apiFilters = useMemo(() => {
    const filters: { search?: string } = {};
    if (searchTerm) {
      filters.search = searchTerm;
    }
    return filters;
  }, [searchTerm]);

  const { packages: allPackages, isLoading, error, refetch } = usePackages(apiFilters);
  const { mutate: deletePkg } = useDeletePackage();

  // Count per type (from ALL fetched packages)
  const typeCounts = useMemo(() => {
    const counts: Record<string, number> = { ALL: allPackages.length };
    for (const type of PACKAGE_TYPES) {
      counts[type] = allPackages.filter(p => p.type === type).length;
    }
    return counts;
  }, [allPackages]);

  // Client-side filter by active tab
  const packages = useMemo(() => {
    if (activeTab === 'ALL') return allPackages;
    return allPackages.filter(p => p.type === activeTab);
  }, [allPackages, activeTab]);

  const handleDelete = async (id: string) => {
    if (!window.confirm(t('common.confirmDelete') ?? 'Are you sure you want to delete this item?'))
      return;
    try {
      await deletePkg(id);
      await refetch();
    } catch {
      // Error is handled in hook
    }
  };

  const handleCopy = (pkg: Package) => {
    navigate(`/packages/new?copyFrom=${pkg.id}`);
  };

  const tabs: { key: TabKey; labelKey: string }[] = [
    { key: 'ALL', labelKey: 'common.all' },
    ...PACKAGE_TYPES.map(type => ({
      key: type as TabKey,
      labelKey: `packages.type.${type.toLowerCase()}`,
    })),
  ];

  return (
    <div className="min-h-screen kgc-bg">
      {/* Header */}
      <header className="bg-white shadow-sm dark:bg-[#1a4f5c]">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3">
            <Button variant="ghost" onClick={() => navigate('/dashboard')}>
              {t('common.back')}
            </Button>
            <div>
              <h1 className="text-xl font-bold text-foreground">{t('packages.title')}</h1>
              <p className="text-sm text-muted-foreground">{t('packages.subtitle')}</p>
            </div>
          </div>
          <Button
            onClick={() => navigate('/packages/new')}
            className="bg-primary hover:bg-primary/90"
          >
            + {t('packages.createNew')}
          </Button>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        {/* Type Tabs */}
        <div className="mb-6 flex flex-wrap gap-1 rounded-lg bg-gray-100 p-1 dark:bg-gray-800">
          {tabs.map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-1.5 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                activeTab === tab.key
                  ? 'bg-white text-foreground shadow dark:bg-[#1a4f5c]'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {t(tab.labelKey)}
              <span
                className={`inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full px-1 text-xs font-medium ${
                  activeTab === tab.key
                    ? 'bg-primary/10 text-primary'
                    : 'bg-gray-200 text-muted-foreground dark:bg-gray-700'
                }`}
              >
                {typeCounts[tab.key] ?? 0}
              </span>
            </button>
          ))}
        </div>

        {/* Search */}
        <div className="mb-6">
          <Input
            placeholder={t('packages.searchPlaceholder')}
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="max-w-md"
          />
        </div>

        {/* Loading */}
        {isLoading && (
          <div className="py-12 text-center text-muted-foreground">{t('common.loading')}</div>
        )}

        {/* Error */}
        {error && (
          <div className="py-12 text-center">
            <p className="text-red-600 dark:text-red-400">{error}</p>
            <Button variant="ghost" onClick={() => void refetch()} className="mt-2">
              {t('common.retry')}
            </Button>
          </div>
        )}

        {/* Empty State */}
        {!isLoading && !error && packages.length === 0 && (
          <div className="py-12 text-center text-muted-foreground">
            <p>{t('packages.noPackages')}</p>
          </div>
        )}

        {/* Package Cards Grid */}
        {!isLoading && !error && packages.length > 0 && (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {packages.map(pkg => (
              <PackageCard
                key={pkg.id}
                pkg={pkg}
                onDetails={() => navigate(`/packages/${pkg.id}`)}
                onEdit={() => navigate(`/packages/${pkg.id}/edit`)}
                onCopy={() => handleCopy(pkg)}
                onDelete={() => void handleDelete(pkg.id)}
                t={t}
              />
            ))}
          </div>
        )}

        {/* Count */}
        {!isLoading && packages.length > 0 && (
          <p className="mt-4 text-sm text-muted-foreground">
            {t('packages.displayedCount', { count: String(packages.length) })}
          </p>
        )}
      </main>
    </div>
  );
}

// ─── Package Card ────────────────────────────────────────────────────────────

interface PackageCardProps {
  pkg: Package;
  onDetails: () => void;
  onEdit: () => void;
  onCopy: () => void;
  onDelete: () => void;
  t: (key: string, params?: Record<string, string>) => string;
}

function PackageCard({ pkg, onDetails, onEdit, onCopy, onDelete, t }: PackageCardProps) {
  const daysLeft =
    pkg.type === 'PROMOTIONAL' ? getSeasonalCountdown(pkg.validTo ?? undefined) : null;
  const displayPrice = pkg.dailyRate ?? pkg.fixedPrice ?? 0;

  return (
    <Card className="group cursor-pointer transition-shadow hover:shadow-md" onClick={onDetails}>
      <CardContent className="pt-6">
        {/* Header: Type badge + Status */}
        <div className="mb-3 flex items-start justify-between">
          <Badge className={TYPE_COLORS[pkg.type]}>
            {t(`packages.type.${pkg.type.toLowerCase()}`)}
          </Badge>
          <Badge className={STATUS_COLORS[pkg.isActive ? 'ACTIVE' : 'INACTIVE']}>
            {t(`packages.status.${pkg.isActive ? 'active' : 'inactive'}`)}
          </Badge>
        </div>

        {/* Name + Code */}
        <h3 className="mb-1 text-lg font-semibold text-foreground">{pkg.name}</h3>
        <p className="mb-3 text-sm text-muted-foreground">{pkg.packageCode}</p>

        {/* Items count */}
        <div className="mb-3 flex items-center gap-2 text-sm text-muted-foreground">
          <span>{t('packages.itemCount', { count: String(pkg.items.length) })}</span>
        </div>

        {/* Price + Discount */}
        <div className="mb-3 flex items-baseline gap-2">
          <span className="text-xl font-bold text-foreground">{formatHuf(displayPrice)}</span>
          {(pkg.discountPercent ?? 0) > 0 && (
            <Badge className="bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300">
              -{Math.round(pkg.discountPercent ?? 0)}%
            </Badge>
          )}
        </div>

        {/* Pricing Model */}
        <div className="mb-3 text-xs text-muted-foreground">
          {t(`packages.pricingModel.${pkg.pricingModel.toLowerCase()}`)}
          {pkg.pricingModel === 'FIXED' && pkg.fixedPrice != null && (
            <span> &mdash; {formatHuf(pkg.fixedPrice)}</span>
          )}
        </div>

        {/* Seasonal countdown */}
        {daysLeft !== null && (
          <div
            className={`mb-3 text-sm font-medium ${
              daysLeft <= 3
                ? 'text-red-600 dark:text-red-400'
                : daysLeft <= 7
                  ? 'text-orange-600 dark:text-orange-400'
                  : 'text-blue-600 dark:text-blue-400'
            }`}
          >
            {daysLeft === 0
              ? t('packages.expired')
              : t('packages.daysLeft', { count: String(daysLeft) })}
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-2 border-t pt-3 dark:border-slate-700">
          <Button
            variant="ghost"
            size="sm"
            onClick={e => {
              e.stopPropagation();
              onDetails();
            }}
          >
            {t('common.details')}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={e => {
              e.stopPropagation();
              onEdit();
            }}
          >
            {t('common.edit')}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={e => {
              e.stopPropagation();
              onCopy();
            }}
          >
            {t('packages.copy')}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="text-red-600 hover:text-red-700 dark:text-red-400"
            onClick={e => {
              e.stopPropagation();
              onDelete();
            }}
          >
            {t('common.delete')}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
